/* eslint react-hooks/exhaustive-deps: "warn" */

import { useContext } from 'react';

import { ContentContext } from 'context/ContentContext';

import { Button } from 'ui/atoms/Button/Button';
import { Faq } from 'ui/atoms/Faq/Faq';
import { Highlight } from 'ui/atoms/Highlight/Highlight';
import { TitleBlock } from 'ui/atoms/TitleBlock/TitleBlock';
import { StatsBlock } from 'ui/molecules/StatsBlock/StatsBlock';
import { NumberedFeatureCards } from 'ui/organisms/NumberedFeatureCards/NumberedFeatureCards';

import { EnrichmentDemoPanel } from 'features/enrichment/atoms/EnrichmentDemoPanel/EnrichmentDemoPanel';
import { EnrichmentTrustBar } from 'features/enrichment/atoms/EnrichmentTrustBar/EnrichmentTrustBar';
import { EnrichmentFinalCtaSection } from 'features/enrichment/organisms/EnrichmentFinalCtaSection/EnrichmentFinalCtaSection';
import { EnrichmentMatchingSection } from 'features/enrichment/organisms/EnrichmentMatchingSection/EnrichmentMatchingSection';
import { EnrichmentProofSection } from 'features/enrichment/organisms/EnrichmentProofSection/EnrichmentProofSection';
import { EnrichmentUseCasesSection } from 'features/enrichment/organisms/EnrichmentUseCasesSection/EnrichmentUseCasesSection';
import { WaysToEnrichSection } from 'features/enrichment/organisms/WaysToEnrichSection/WaysToEnrichSection';

import styles from './EnrichmentPage.module.scss';

import { FAQ_DATA } from './data';

const EnrichmentPage = () => {
  const { secondaryNavigation } = useContext(ContentContext);

  return (
    <div className={styles.root}>
      <section className={styles.hero}>
        <div className={styles.heroGlow} aria-hidden="true" />

        <div className={styles.wrap}>
          <div className={styles.heroGrid}>
            <div className={styles.heroCopy}>
              <TitleBlock
                size="page"
                align="left"
                title="Enrich every record. Officially."
                description={
                  <>
                    Match any name, domain or registration number to its official registry record. Up to{' '}
                    <strong>40 fields appended from 400+ government registries</strong> across 200+ countries.
                  </>
                }
              />

              {secondaryNavigation?.free_trial?.slug && (
                <Button type="blue" size="xl" route={secondaryNavigation.free_trial.slug} className={styles.cta}>
                  Enrich a sample file
                </Button>
              )}
            </div>

            <div className={styles.heroDemo}>
              <EnrichmentDemoPanel />
            </div>
          </div>

          <EnrichmentTrustBar />
        </div>
      </section>

      <WaysToEnrichSection />

      <NumberedFeatureCards
        theme="light"
        eyebrow="What gets appended"
        title="Six data categories. One source of truth."
        columns={3}
        items={[
          {
            num: '01',
            title: 'Company registry data',
            description:
              'Legal name & previous names · Registration number & VAT / tax IDs · Legal form & incorporation date · Company status · Registered & trading addresses · Registry source & filing link',
          },
          {
            num: '02',
            title: 'Financials',
            description:
              'Filed revenue & turnover · Assets, liabilities & net worth · Profit & loss figures · Filing history & latest accounts · Currency & reporting period · Multi-year financial trend',
          },
          {
            num: '03',
            title: 'Firmographics',
            description:
              'Industry codes — SIC, NACE, NAICS · Employee counts & size bands · Company age & trading status · Websites & digital footprint · Phone numbers & contacts · Geographic & jurisdiction data',
          },
          {
            num: '04',
            title: 'Directors & shareholders',
            description:
              'Directors & active officers · Appointment & resignation dates · Shareholders & shareholding % · Persons of significant control · Ultimate beneficial owners · Officer roles & nationalities',
          },
          {
            num: '05',
            title: 'Group structure',
            description:
              'Immediate parent company · Domestic ultimate · Global ultimate owner · Subsidiaries & branches · Full corporate linkage tree · Cross-registry resolution',
          },
          {
            num: '06',
            title: 'Contact data',
            description:
              'Key executive names & titles · Business email addresses · Direct & company phone numbers · LinkedIn profiles · Department & seniority · Confidence-scored contacts',
          },
        ]}
        actions={
          <Button type="text" route="/data-dictionary" suffixIcon="arrow-forward">
            Explore all 40 fields in the data dictionary
          </Button>
        }
      />

      <StatsBlock
        theme="dark"
        extra={
          <Highlight color="lightBlue" font="mono" uppercase>
            Coverage
          </Highlight>
        }
        title="Built on primary sources at global scale"
        features={[
          { value: '600', suffix: 'M+', title: 'Company records' },
          { value: '400', suffix: '+', title: 'Government registries' },
          { value: '200', suffix: '+', title: 'Countries & territories' },
          { value: '100', suffix: '%', title: 'Source-cited fields' },
        ]}
      />

      <EnrichmentMatchingSection />

      <EnrichmentUseCasesSection />

      <EnrichmentProofSection />

      <EnrichmentFinalCtaSection />

      <Faq className={styles.faq} title="Data enrichment, answered" data={FAQ_DATA} />
    </div>
  );
};

// eslint-disable-next-line import/no-default-export
export default EnrichmentPage;
